import { useRef, useContext, useEffect, useState } from "react";
// user
import { UserContext } from "@src/App";
// hooks
import { useBroadcastChannel } from "@src/utils/hooks/useBroadcastChannel";
import { useInterWindow } from "@src/utils/hooks/useInterWindow";
// photo
import { TPhotoType } from "./Interface";
import { PhotoProviderData } from "./Provider";
import { usePhotoActions } from "./usePhotoActions";
// components
import Blank from "@src/components/common/blank/Blank";
import { PhotoTab } from "./ControlPanel";
import { OfficialPhotoWrap, ReviewPhotoWrap } from "./Wrap";
import { Wrap } from "@src/shared/src";
import AppHeader from "@src/components/common/appHeader/AppHeader";
import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";

// 포토 영역
const PhotoListSection = ({ activeParentTabId }: { activeParentTabId: number }) => {
  // 폴더 모달 열기 기본 설정
  const { openWindow: openFolder } = useInterWindow({
    id: "photoListSection",
    receiverId: "folder",
    callBackWhenBackToOriginalWindow: () => {
      setSelectedFolder(null);
    },
  });

  // 이미지 모달 열기 기본 설정
  const { openWindow: openImage } = useInterWindow({
    id: "photoListSection",
    receiverId: "image",
    callBackWhenBackToOriginalWindow: () => {
      setSelectedPhoto(null);
    },
  });

  // provider data
  const {
    partner,
    selectedPhoto,
    setSelectedPhoto,
    officialPhotoData,
    reviewPhotoData,
    selectedFolder,
    setSelectedFolder,
    partnerType, // 파트너 타입
  } = useContext(PhotoProviderData).data;

  // folder 이미지 호출
  const { getFolderPhotoByType } = usePhotoActions();

  // 추가 호출 필요 데이터 타입
  const [needMoreDataType, setNeedMoreDataType] = useState<TPhotoType | null>(null);

  // user
  const user = useContext(UserContext);

  // 포토모달 분기 처리
  const isPhotoMain = window.location.pathname.includes("photo"); // 포토모달 분기 처리

  // 초기 스크롤 위치
  const initialScrollTop = useRef(0);

  // 컴포넌트 참조
  const photoListSectionRef = useRef(null);

  // 이미지 데이터 변경시 상대창에게 데이터 업데이트
  const { broadcastMessage: broadcastImageMessage } = useBroadcastChannel(
    "image",
    (e: MessageEvent) => {
      if (e?.data?.type === "notifySenderForDataUpdate") {
        if (needMoreDataType) return; // 중복 호출 방지
        if (e?.data?.photoType === "official") {
          setNeedMoreDataType("official");
        } else {
          setNeedMoreDataType("review");
        }
      }
    },
  );

  // 컴포넌트 마운트시 스크롤 위치 계산
  useEffect(() => {
    if (photoListSectionRef?.current) {
      const offsetTop = photoListSectionRef.current?.contents?.offsetTop || 0;
      initialScrollTop.current = offsetTop - 106;
    }
  }, [photoListSectionRef]);

  // 선택된 포토 데이터 있을 시 이미지 모달 열기
  useEffect(() => {
    if (selectedPhoto?.id) {
      openImage(
        {
          photoData: selectedPhoto,
        },
        {
          url: `/image`,
          hidetop: true,
          hidesafearea: false,
        },
      );
    }
  }, [selectedPhoto?.id]);

  // needMoreDataType 정상동작하도록 포토데이터 등록 시 초기화
  useEffect(() => {
    if (selectedPhoto?.photoData) {
      setNeedMoreDataType(null);
    }
  }, [selectedPhoto?.photoData]);
  // needMoreDataType 정상동작하도록 needMoreDataType에 값이 들어온 경우 바로 초기화
  useEffect(() => {
    if (needMoreDataType) {
      setNeedMoreDataType(null);
    }
  }, [needMoreDataType]);

  // 리뷰, 공식 사진 데이터 변경시 상대창에게 데이터 업데이트
  useEffect(() => {
    broadcastImageMessage({
      type: "sendUpdatedDataToReceiver",
      newPhotoData: reviewPhotoData,
      photoType: "review",
    });
  }, [reviewPhotoData]);
  useEffect(() => {
    broadcastImageMessage({
      type: "sendUpdatedDataToReceiver",
      newPhotoData: officialPhotoData,
      photoType: "official",
    });
  }, [officialPhotoData]);

  // selectedFolder.id 변경시, selectedFolder.photoData가 없을 경우 데이터 호출, 있을 경우 모달 열기
  useEffect(() => {
    if (!selectedFolder) return;
    if (selectedFolder?.id && !selectedFolder.photoData) {
      if (partnerType === "weddinghall") {
        getFolderPhotoByType("init", {
          params: { uuid: user.me.uuid, partnerUuid: partner?.uuid, key: selectedFolder.key },
          partnerUuid: partner?.uuid,
          //   type: selectedFolder.key,
        });
      } else {
        getFolderPhotoByType("init", {
          albumId: selectedFolder.key,
          fetchSize: 1000,
          fetchStart: 0,
          uuid: user.me.uuid,
        });
      }
    } else {
      openFolder(
        {
          photoData: selectedFolder,
          columnCount: 2, // 행 갯수
          columnGap: 12, // 행 간격
          onClickType: "photo",
        },
        {
          url: `/folder`,
          hidetop: true,
          hidesafearea: false,
        },
      );
    }
  }, [selectedFolder?.id]);

  // 로딩 스피너 표시 여부
  const [showLoader, setShowLoader] = useState<boolean>(false);

  return (
    <>
      {isPhotoMain ? (
        <Wrap
          ref={photoListSectionRef}
          className="photo-modal-wrap-main"
          fullHeight={true}
          viewportResize={false}
          headerComponent={
            <AppHeader type="stack" title={`${partner?.bizName ? partner.bizName : ""}`} />
          }
          contentsComponent={{
            contents: (
              <>
                <Blank height={4} />
                <div className="photo-list-wrap photo-basic-wrap">
                  {user?.me && (
                    <>
                      {/* 사진 탭 */}
                      <PhotoTab />
                      {/* 공식 사진 */}
                      <OfficialPhotoWrap
                        activeParentTabId={activeParentTabId}
                        triggerAddData={needMoreDataType === "official"}
                        initialScrollTop={initialScrollTop.current}
                        showLoader={(state) => setShowLoader(state)}
                        photoListRef={photoListSectionRef}
                      />
                      {/* 리뷰 사진 */}
                      <ReviewPhotoWrap
                        activeParentTabId={activeParentTabId}
                        triggerAddData={needMoreDataType === "review"}
                        initialScrollTop={initialScrollTop.current}
                        showLoader={(state) => setShowLoader(state)}
                        photoListRef={photoListSectionRef}
                      />
                    </>
                  )}
                </div>
              </>
            ),
            extra: <>{showLoader && <LoadingSpinner />}</>,
          }}
        />
      ) : (
        <div ref={photoListSectionRef}>
          <Blank height={4} />
          <div className="photo-list-wrap photo-basic-wrap">
            {user?.me && (
              <>
                {/* 사진 탭 */}
                <PhotoTab />
                {/* 공식 사진 */}
                <OfficialPhotoWrap
                  activeParentTabId={activeParentTabId}
                  triggerAddData={needMoreDataType === "official"}
                  initialScrollTop={initialScrollTop.current}
                  showLoader={(state) => setShowLoader(state)}
                  photoListRef={null}
                />
                {/* 리뷰 사진 */}
                <ReviewPhotoWrap
                  activeParentTabId={activeParentTabId}
                  triggerAddData={needMoreDataType === "review"}
                  initialScrollTop={initialScrollTop.current}
                  showLoader={(state) => setShowLoader(state)}
                  photoListRef={null}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoListSection;
